/**
 * CORE-1730 — поле черновика мобильного веб-композера, в котором эмодзи
 * комплекта видны картинками (решение владельца 21.10: «тот же выбранный
 * смайлик», только на ширине <768). CORE-1791 (возврат QA 23.10) — в разделе
 * сообщений это поле на любой ширине: в макете v3 черновик тоже с картинкой.
 *
 * Внутри `contentEditable`; наружу — тот же текст с токенами `:agape_N:`,
 * что отдавало текстовое поле, поэтому черновик, упоминания, быстрые ответы
 * и редактирование работают как раньше. Пока человек печатает, DOM не
 * перестраивается (иначе ломается составной ввод Android); перестройка
 * только когда текст пришёл снаружи и отличается от того, что поле отдало.
 * Только веб: на нативе возвращает null, тред его туда не ставит.
 */
import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { Platform, View } from 'react-native';
import { Asset } from 'expo-asset';
import { reactionOf } from '../../constants/brandReactions';
import {
  DRAFT_TOKEN_ATTR, draftCaretInfo, draftNodesFromText, draftPointAt, draftTextFromNodes,
  insertDraftToken, removeDraftTokenBefore, type DraftNodeFactory, type DraftNodeLike,
} from './draftFieldDom';

export interface DraftFieldHandle {
  focus: () => void;
  blur: () => void;
  /** Как у textarea: упоминания ставят курсор в конец после выбора подсказки. */
  setSelectionRange: (start: number, end: number) => void;
  /**
   * CORE-1791 — картинка комплекта из палитры встаёт туда, где стоял курсор
   * (или в конец, если курсора в поле не было), а не всегда в конец текста.
   */
  insertToken: (token: string) => void;
}

interface Props {
  value: string;
  onChangeText: (text: string) => void;
  /** Enter без Shift — отправка, как у текстового поля. */
  onSubmit: () => void;
  placeholder: string;
  placeholderColor: string;
  color: string;
  /** Нет кнопки скрепки спереди — текст отодвинут от скругления поля. */
  leading: boolean;
  minHeight: number;
  maxHeight: number;
  /** CORE-1791 — сторона картинки эмодзи в черновике (раздел сообщений — 28). */
  glyphSize?: number;
  /** CORE-1791 — внутренние отступы поля (CSS), когда их задаёт материал раздела. */
  padding?: string;
  testID?: string;
}

/** Картинка в строке как у `ReactionGlyph` inline: 22px, чуть ниже базовой линии. */
const GLYPH = 22;
const CARET_ANCHOR = '\u200B';
const uriCache = new Map<string, string>();

function glyphUri(type: string): string | undefined {
  const cached = uriCache.get(type);
  if (cached) return cached;
  const reaction = reactionOf(type);
  if (!reaction) return undefined;
  const uri = Asset.fromModule(reaction.image as number).uri;
  uriCache.set(type, uri);
  return uri;
}

function nodeFactory(doc: Document, glyph = GLYPH): DraftNodeFactory<Node> {
  return {
    text: (value) => doc.createTextNode(value),
    lineBreak: () => doc.createElement('br'),
    image: (token) => {
      const uri = glyphUri(token);
      if (!uri) return null;
      const img = doc.createElement('img');
      img.src = uri;
      img.alt = reactionOf(token)?.label ?? token;
      img.setAttribute(DRAFT_TOKEN_ATTR, token);
      img.draggable = false;
      img.style.cssText = `width:${glyph}px;height:${glyph}px;vertical-align:-${Math.round(glyph * 0.2)}px;margin:0 ${glyph === GLYPH ? 1 : 2}px;`;
      return img;
    },
  };
}

/** Курсор в поле — по смещению в тексте черновика (после перестройки DOM). */
function placeCaretAt(el: HTMLElement, offset: number) {
  const doc = el.ownerDocument;
  const sel = doc.getSelection();
  if (!sel) return;
  const point = draftPointAt(el as unknown as DraftNodeLike, offset);
  const range = doc.createRange();
  range.setStart(point.container as unknown as Node, point.offset);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

function placeCaretAtEnd(el: HTMLElement) {
  const doc = el.ownerDocument;
  const sel = doc.getSelection();
  if (!sel) return;
  const range = doc.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Текст → DOM поля; после картинки или переноса в конце — якорь, чтобы курсор мог встать за ними. */
function renderText(el: HTMLElement, text: string, glyph?: number) {
  const doc = el.ownerDocument;
  el.replaceChildren(...draftNodesFromText(text, nodeFactory(doc, glyph)));
  if (el.lastChild && el.lastChild.nodeType === 1) el.appendChild(doc.createTextNode(CARET_ANCHOR));
}

export const DraftField = forwardRef<DraftFieldHandle, Props>(function DraftField(
  { value, onChangeText, onSubmit, placeholder, placeholderColor, color, leading, minHeight, maxHeight, glyphSize, padding, testID },
  ref,
) {
  const el = useRef<HTMLDivElement>(null);
  /** Что поле отдало последним: совпадает с `value` — DOM не трогаем. */
  const lastText = useRef<string | null>(null);
  /**
   * Где был курсор, когда поле потеряло фокус. Кнопка палитры фокус не
   * забирает, но сами эмодзи палитры — кнопки, и щелчок по ним уводит фокус;
   * без этого вставка не знала бы, куда встать.
   */
  const lastCaret = useRef<number | null>(null);

  /** Смещение курсора в тексте черновика, если выделение сейчас внутри поля. */
  const caretNow = useCallback((): number | null => {
    const node = el.current;
    const sel = node?.ownerDocument.getSelection();
    if (!node || !sel || sel.rangeCount === 0 || !node.contains(sel.anchorNode)) return null;
    const range = sel.getRangeAt(0);
    return draftCaretInfo(node as unknown as DraftNodeLike, {
      container: range.startContainer as unknown as DraftNodeLike, offset: range.startOffset,
    }).offset;
  }, []);

  /** Текст поменялся не набором (палитра, Backspace за картинкой): DOM собирается заново. */
  const replaceText = useCallback((text: string, caret: number) => {
    const node = el.current;
    if (!node) return;
    renderText(node, text, glyphSize);
    lastText.current = text;
    lastCaret.current = caret;
    node.focus();
    placeCaretAt(node, caret);
    onChangeText(text);
  }, [glyphSize, onChangeText]);

  useImperativeHandle(ref, () => ({
    focus: () => el.current?.focus(),
    blur: () => el.current?.blur(),
    setSelectionRange: () => { if (el.current) placeCaretAtEnd(el.current); },
    insertToken: (token: string) => {
      const node = el.current;
      if (!node) return;
      const text = draftTextFromNodes(node as unknown as DraftNodeLike);
      const at = caretNow() ?? lastCaret.current ?? text.length;
      const next = insertDraftToken(text, Math.min(at, text.length), token);
      replaceText(next.text, next.caret);
    },
  }), [caretNow, replaceText]);

  useLayoutEffect(() => {
    const node = el.current;
    if (!node || value === lastText.current) return;
    renderText(node, value, glyphSize);
    lastText.current = value;
    // Текст пришёл снаружи (отправка, другой диалог, правка) — прежний курсор к нему не относится.
    lastCaret.current = null;
    if (node.ownerDocument.activeElement === node) placeCaretAtEnd(node);
  }, [value, glyphSize]);

  const emit = useCallback(() => {
    const node = el.current;
    if (!node) return;
    const text = draftTextFromNodes(node);
    lastText.current = text;
    onChangeText(text);
  }, [onChangeText]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit();
      return;
    }
    // CORE-1791 — за картинкой в конце стоит невидимый якорь курсора, и первый
    // Backspace стирал его, а картинка оставалась. Картинку перед курсором
    // убираем сами, одним нажатием; остальное стирает браузер как обычно.
    if (e.key === 'Backspace' && !e.nativeEvent.isComposing && !e.altKey && !e.metaKey && !e.ctrlKey) {
      const node = el.current;
      const sel = node?.ownerDocument.getSelection();
      if (!node || !sel || sel.rangeCount === 0 || !sel.isCollapsed || !node.contains(sel.anchorNode)) return;
      const range = sel.getRangeAt(0);
      const info = draftCaretInfo(node as unknown as DraftNodeLike, {
        container: range.startContainer as unknown as DraftNodeLike, offset: range.startOffset,
      });
      if (!info.tokenBefore) return;
      const next = removeDraftTokenBefore(info.text, info.offset, info.tokenBefore);
      if (!next) return;
      e.preventDefault();
      replaceText(next.text, next.caret);
    }
  }, [onSubmit, replaceText]);

  const rememberCaret = useCallback(() => {
    const at = caretNow();
    if (at !== null) lastCaret.current = at;
  }, [caretNow]);

  // CORE-1791 — в буфер уходит текст с токенами (`:agape_3:`), а не подписи
  // картинок: иначе вставка возвращала «Благодарность» словом, и черновик с
  // ним же уходил в хранилище — картинка терялась насовсем.
  const copySelection = useCallback((e: React.ClipboardEvent<HTMLDivElement>, cut: boolean) => {
    const node = el.current;
    const sel = node?.ownerDocument.getSelection();
    if (!node || !sel || sel.rangeCount === 0 || sel.isCollapsed || !node.contains(sel.anchorNode)) return;
    const range = sel.getRangeAt(0);
    e.preventDefault();
    e.clipboardData.setData('text/plain', draftTextFromNodes(range.cloneContents() as unknown as DraftNodeLike));
    if (!cut) return;
    range.deleteContents();
    emit();
  }, [emit]);
  const onCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => copySelection(e, false), [copySelection]);
  const onCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => copySelection(e, true), [copySelection]);

  // Из буфера — только текст; токены в нём сразу становятся картинками.
  const onPaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const node = el.current;
    const text = e.clipboardData.getData('text/plain');
    if (!node || !text) return;
    const doc = node.ownerDocument;
    const fragment = doc.createDocumentFragment();
    draftNodesFromText(text, nodeFactory(doc, glyphSize)).forEach((n) => fragment.appendChild(n));
    const tail = fragment.lastChild;
    const sel = doc.getSelection();
    if (sel && sel.rangeCount > 0 && node.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(fragment);
      if (tail) {
        range.setStartAfter(tail);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } else {
      node.appendChild(fragment);
      placeCaretAtEnd(node);
    }
    emit();
  }, [emit, glyphSize]);

  if (Platform.OS !== 'web') return null;
  // Подсказка стоит там же, где начнётся текст: по заданным отступам или прежним.
  const [padTop, padLeft] = padding
    ? (() => { const v = padding.split(/\s+/).map((x) => parseFloat(x) || 0); return [v[0], v[v.length > 3 ? 3 : v.length > 1 ? 1 : 0]]; })()
    : [10, leading ? 14 : 4];
  return (
    <View style={{ flex: 1, minWidth: 0 }}>
      <div
        ref={el}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        aria-placeholder={placeholder}
        data-testid={testID}
        enterKeyHint="send"
        spellCheck
        onInput={emit}
        onKeyDown={onKeyDown}
        onKeyUp={rememberCaret}
        onMouseUp={rememberCaret}
        onBlur={rememberCaret}
        onPaste={onPaste}
        onCopy={onCopy}
        onCut={onCut}
        style={{
          minHeight, maxHeight, overflowY: 'auto', outline: 'none', boxSizing: 'border-box',
          padding: padding ?? `10px 4px 10px ${padLeft}px`,
          fontFamily: 'var(--ag-font-ui)', fontWeight: 400, fontSize: 16, lineHeight: '24px', color,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', caretColor: color,
          WebkitUserSelect: 'text', userSelect: 'text',
        }}
      />
      {value === '' && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', left: padLeft, top: padTop, pointerEvents: 'none',
            // Подсказка — одна строка: на 320 px «Ваше сообщение…» переносилось и
            // раздувало поле выше набранного текста.
            right: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            fontFamily: 'var(--ag-font-ui)', fontWeight: 400, fontSize: 16, lineHeight: '24px', color: placeholderColor,
          }}
        >
          {placeholder}
        </div>
      )}
    </View>
  );
});
